import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NexovernLogo from '@/assets/nexovern-full.png';
import { useAuthStore } from '@/store/auth.store';

/**
 * Landing page for the SSO redirect.
 *
 * The backend parks the issued token pair in Redis under a single-use code and
 * sends that code back in the URL *fragment* — browsers never transmit a
 * fragment to a server, so it stays out of access logs and Referer headers.
 * This page reads it, wipes it from the address bar before anything else, and
 * trades it for the same token pair a password login returns.
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const completeSsoLogin = useAuthStore((s) => s.completeSsoLogin);
  const [failed, setFailed] = useState(false);

  // React 18+ mounts effects twice in StrictMode. The handoff code is consumed
  // on first use, so without this guard the second run always fails and would
  // bounce a perfectly good sign-in back to the login page.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const code = params.get('code');
    const returnTo = params.get('returnTo') ?? '/dashboard';

    // Strip the fragment immediately so the code is not left sitting in the
    // address bar, in session history, or in anything the user might copy.
    window.history.replaceState(null, '', window.location.pathname);

    if (!code) {
      setFailed(true);
      navigate('/login?error=sso_failed', { replace: true });
      return;
    }

    completeSsoLogin(code)
      .then(() => {
        // Guard against an open redirect: only same-origin absolute paths.
        const safe = returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/dashboard';
        navigate(safe, { replace: true });
      })
      .catch(() => {
        setFailed(true);
        navigate('/login?error=sso_failed', { replace: true });
      });
  }, [completeSsoLogin, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-1 px-4">
      <div className="flex flex-col items-center gap-4">
        <img src={NexovernLogo} alt="Nexovern" className="h-8 w-auto" />
        {failed ? (
          <p role="alert" className="text-xs text-state-danger">
            Sign-in failed. Returning to the login page…
          </p>
        ) : (
          <>
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent border-t-transparent" />
            <p role="status" className="text-xs text-text-tertiary">
              Completing sign-in…
            </p>
          </>
        )}
      </div>
    </div>
  );
}
